import React, {useState} from 'react';
import styles from '@dazn/lp-web/src/components/Redeem/redeem-page.module.css';
import Markdown from 'markdown-to-jsx';
import classNames from 'classnames';
import Button, {ButtonTypes} from '../Button/Button';
import StaticImage from '../Images/ImageComponent/Image';
import {useLandingPagesContext} from '../../contexts/LandingPageContext';
import {navigateToDifferentChapterUrl} from '../../utils';
import {DaznChapterEnum} from '../../types/dazn/DaznChapterEnum';
import {DaznChapterData} from '../../types/dazn/DaznChapterData';
import {isproductRedeemV2} from '../Header/Utils/features';

interface RedeemPageProps {
    data: any;
    key: number;
    disableAnimation: boolean;
}
const RedeemPage: React.FC<RedeemPageProps> = ({data}) => {
    const {redeem} = data;
    const {
        title,
        subtitle,
        inputPlaceholder,
        supportText,
        image,
        componentType,
        description,
        errorText,
        order,
        ctaButton
    } = redeem;

    const {verifyGiftCode} = useLandingPagesContext();

    const [isDisabled, setIsDisabled] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [isErrorCode, setIsErrorCode] = useState(false);

    const toCamelCase = (str: string) => {
        return str.charAt(0).toLowerCase() + str.slice(1);
    };

    const transformResponse = (response: Record<string, any>) => {
        return Object.keys(response).reduce((acc, key) => {
            acc[toCamelCase(key)] = response[key];
            return acc;
        }, {} as Record<string, any>);
    };
    async function handleRedeem() {
        if (!verifyGiftCode) {
            throw new Error('LandingPageContext is not provided');
        }

        setIsDisabled(true);

        try {
            const verifyResult = await verifyGiftCode(inputValue);
            const currentChapter = window.dazn.chapterHistory.currentChapter;
            if (!verifyResult.GiftCode) {
                setIsErrorCode(true);
            } else {
                const formattedGiftCode = transformResponse(verifyResult);
                formattedGiftCode.type = formattedGiftCode.type ?? 'standard';

                const chapterData = {
                    [DaznChapterData.GiftCode]: formattedGiftCode,
                    [DaznChapterData.PromoCode]: verifyResult.promoCode ?? '',
                    [DaznChapterData.CampaignCode]:
                        verifyResult.campaignCode ?? 'generic'
                };

                if (
                    typeof currentChapter === 'object' &&
                    currentChapter.chapterData?.authNflGCJourney
                ) {
                    navigateToDifferentChapterUrl({
                        chapterName: DaznChapterEnum.Auth,
                        chapterPath: 'account/content/NFL/signup',
                        chapterData
                    });
                } else if (verifyResult.ProductGroup === 'NFL') {
                    navigateToDifferentChapterUrl({
                        chapterName: isproductRedeemV2()
                            ? DaznChapterEnum.Auth
                            : DaznChapterEnum.Catalog,
                        chapterPath: isproductRedeemV2()
                            ? '/account/content/NFL/signup'
                            : '/home',
                        chapterData
                    });
                } else {
                    navigateToDifferentChapterUrl({
                        chapterName: DaznChapterEnum.Auth,
                        chapterPath: 'account/signup',
                        chapterData
                    });
                }
            }
        } catch (error) {
            console.error('Error verifying gift code:', error);
        } finally {
            setIsDisabled(false);
        }
    }

    const onGiftCodeChange = function (
        event: React.ChangeEvent<HTMLInputElement>
    ): void {
        const newInputValue = event.target.value;
        const startupDataRegex = window.dazn.startupData.RegularExpressions;
        const giftCodeLengthRegex = startupDataRegex.find(
            (rule) => rule.Key === 'gift-code-length'
        );
        const giftCodeCharsRegex = startupDataRegex.find(
            (rule) => rule.Key === 'gift-code-chars'
        );
        const giftCodeLength = giftCodeLengthRegex
            ? new RegExp(
                  giftCodeLengthRegex?.Pattern,
                  giftCodeLengthRegex.Modifiers
              )
            : null;
        const giftCodeChars = giftCodeCharsRegex
            ? new RegExp(
                  giftCodeCharsRegex.Pattern,
                  giftCodeCharsRegex.Modifiers
              )
            : null;

        setInputValue(newInputValue);
        const isValid =
            giftCodeLength?.test(newInputValue) &&
            giftCodeChars?.test(newInputValue);
        setIsDisabled(!isValid);
        setIsErrorCode(false);
    };

    const renderFields = {
        title: () => <h1 className={styles.giftTitle}>{title}</h1>,
        subtitle: () => <p className={styles.giftSubtext}>{subtitle}</p>,
        description: () => (
            <Markdown
                className={styles.CheckIconList}
                data-target="redeem-description"
                options={{
                    forceBlock: true
                }}
            >
                {description}
            </Markdown>
        ),
        inputBox: () => (
            <div className={styles.giftInputWrapper}>
                <input
                    type="text"
                    className={classNames(styles.giftInput, {
                        [styles.error]: isErrorCode
                    })}
                    placeholder={inputPlaceholder}
                    value={inputValue}
                    onChange={onGiftCodeChange}
                    onKeyDown={(e: {key: string}) => {
                        if (e.key === 'Enter') {
                            isDisabled ? undefined : handleRedeem();
                        }
                    }}
                />
                {isErrorCode && (
                    <div className={styles.errorMessage}>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={styles.svgStyle}
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10C20 7.34784 18.9464 4.8043 17.0711 2.92893C15.1957 1.05357 12.6522 0 10 0ZM11 15H9V9H11V15ZM10 7C9.44771 7 9 6.55228 9 6C9 5.44772 9.44771 5 10 5C10.5523 5 11 5.44772 11 6C11 6.55228 10.5523 7 10 7Z"
                                fill="#FD7777"
                            />
                        </svg>
                        <div className={styles.errorText}>
                            <Markdown children={errorText} />
                        </div>
                    </div>
                )}
            </div>
        ),
        Button: () => (
            <Button
                label={ctaButton.buttonLabel}
                onClick={isDisabled ? undefined : handleRedeem}
                className={classNames(styles.giftButton, {
                    [styles.disabledBtn]: isDisabled
                })}
                buttonTheme={ButtonTypes.PRIMARY}
                disabled={isDisabled}
            />
        ),

        legalDisclaimer: () => (
            <Markdown
                className={styles.supportText}
                data-target="redeem-Support"
                options={{
                    forceBlock: true
                }}
            >
                {supportText}
            </Markdown>
        )
    };

    const renderComponents = order.map((item: string) => {
        switch (item) {
            case 'title':
                return renderFields.title();
            case 'subtitle':
                return renderFields.subtitle();
            case 'description':
                return renderFields.description();
            case 'inputBox':
                return renderFields.inputBox();
            case 'Button':
                return renderFields.Button();
            case 'legalDisclaimer':
                return renderFields.legalDisclaimer();
            default:
                throw null;
        }
    });
    return (
        <div className={styles.redeemBackground}>
            <StaticImage
                image={image}
                alt={componentType}
                componentType={componentType}
                showPlaceHolderImage={true}
            />
            <div className={styles.redeemContainer}>{renderComponents}</div>
        </div>
    );
};

export default RedeemPage;
